import useReportSort from '../../components/reports/useReportSort';
import { useMemo, useState } from "react";
import {
  FiAlertCircle,
  FiChevronDown,
  FiChevronUp,
  FiDownload,
  FiFileText,
  FiFilter,
  FiLoader,
  FiRefreshCw,
  FiShield,
  FiUser,
} from "react-icons/fi";

import ReportFilterModal from "../../components/reports/ReportFilterModal";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import { hasPermission } from "../../utils/permissions";

const dateOnly = (value) => new Date(value).toISOString().slice(0, 10);
const firstDay = () => {
  const value = new Date();
  value.setDate(1);
  return dateOnly(value);
};
const initialFilters = () => ({ from: firstDay(), to: dateOnly(new Date()) });
const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
const quantity = (value) =>
  Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });

function downloadFile(blob, name) {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
}

function Field({ label, children }) {
  return (
    <label className="block text-sm font-semibold text-slate-700">
      <span className="mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

export default function CashierWiseReportPage() {
  const { accessToken, user } = useAuth();
  const canGenerate = hasPermission(user, "reports.cashier_wise.generate");
  const canExport = hasPermission(user, "reports.cashier_wise.export");
  const [rawReport, setReport] = useState(null);
  const { report, controls: sortControls } = useReportSort(rawReport);
  const [showFilters, setShowFilters] = useState(false);
  const [draftFilters, setDraftFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [expandedCashiers, setExpandedCashiers] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  const toggleCashier = (cashier) => {
    setExpandedCashiers((current) => {
      const next = new Set(current);
      if (next.has(cashier)) next.delete(cashier);
      else next.add(cashier);
      return next;
    });
  };

  const generate = async () => {
    if (!canGenerate) return;
    setLoading(true);
    setError("");
    try {
      const nextReport = await apiRequest(
        "/business-reports/cashier-wise/generate",
        { method: "POST", body: draftFilters, accessToken },
      );
      setReport(nextReport);
      setAppliedFilters({ ...draftFilters });
      setExpandedCashiers(new Set());
      setShowFilters(false);
    } catch (requestError) {
      setReport(null);
      setError(
        requestError.message || "Could not generate the cashier report.",
      );
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    if (!report || !canExport) return;
    setExporting(true);
    setError("");
    try {
      const response = await apiRequest(
        `/business-reports/cashier-wise/export?${new URLSearchParams({ ...appliedFilters, format: "XLSX" })}`,
        { accessToken, rawResponse: true },
      );
      const name =
        response.headers
          .get("content-disposition")
          ?.match(/filename="?([^";]+)"?/)?.[1] || "charms-cashier-wise.xlsx";
      downloadFile(await response.blob(), name);
    } catch (requestError) {
      setError(requestError.message || "Could not export the cashier report.");
    } finally {
      setExporting(false);
    }
  };

  const summaryCards = useMemo(
    () =>
      report
        ? [
            ["Cashiers", report.summary.cashiers, true, "text-primary-800"],
            ["Bills", report.summary.bills, true, "text-sky-700"],
            [
              "Sales value",
              report.summary.totalAmount,
              false,
              "text-emerald-700",
            ],
            ["GST", report.summary.taxAmount, false, "text-violet-700"],
            ["Received", report.summary.paidAmount, false, "text-amber-700"],
          ]
        : [],
    [report],
  );

  const openFilters = () => {
    setDraftFilters({ ...appliedFilters });
    setShowFilters(true);
  };
  const closeFilters = () => {
    setDraftFilters({ ...appliedFilters });
    setShowFilters(false);
  };

  return (
    <div className="business-report space-y-5">
      <section className="border-b border-slate-200 bg-white px-1 pb-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[.18em] text-primary-800">
              <FiUser /> Business reports
            </div>
            <h1 className="text-3xl font-bold text-slate-950">
              Cashier-wise Report
            </h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-500">
              POS sales by cashier. Select a cashier row to inspect their bills,
              sold products, assigned agents, discount, GST and net amount.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={openFilters}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-primary-300 hover:text-primary-800"
            >
              <FiFilter /> Filters
            </button>
            <button
              type="button"
              onClick={generate}
              disabled={!canGenerate || loading}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-800 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-900 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? (
                <FiLoader className="animate-spin" />
              ) : (
                <FiRefreshCw />
              )}
              {loading ? "Generating…" : "Generate"}
            </button>
            <button
              type="button"
              onClick={exportExcel}
              disabled={!report || !canExport || exporting}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-primary-300 hover:text-primary-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {exporting ? (
                <FiLoader className="animate-spin" />
              ) : (
                <FiDownload />
              )}
              {exporting ? "Preparing…" : "Export Excel"}
            </button>
          </div>
        </div>
      </section>

      {error && (
        <div className="flex gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          {error}
        </div>
      )}
      {!canGenerate && (
        <p className="flex gap-2 rounded-lg bg-amber-50 p-4 text-sm text-amber-800">
          <FiShield className="mt-0.5 shrink-0" />
          You do not have permission to generate this report.
        </p>
      )}

      {!report ? (
        <section className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-20 text-center">
          <FiFileText className="mx-auto mb-4 text-4xl text-primary-300" />
          <h2 className="text-xl font-bold text-slate-900">
            No preview generated yet
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            Choose the POS date range, then select Generate.
          </p>
        </section>
      ) : (
        <>
          <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            {summaryCards.map(([label, value, count, color]) => (
              <div
                key={label}
                className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
              >
                <p className="text-sm text-slate-500">{label}</p>
                <p className={`mt-2 text-2xl font-bold ${color}`}>
                  {count ? Number(value).toLocaleString("en-IN") : money(value)}
                </p>
              </div>
            ))}
          </section>

          <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-col gap-2 border-b border-slate-100 px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-950">
                  Cashier performance
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Click any cashier row to expand their POS bills and product
                  lines.
                </p>
              </div>
              <p className="text-xs font-medium text-slate-500">
                {appliedFilters.from} to {appliedFilters.to}
              </p>
            </div>
            <div className="overflow-x-auto">
                  {sortControls}
              <table className="min-w-[1200px] w-full text-sm">
                <thead className="bg-slate-100 text-left text-xs font-bold uppercase tracking-wide text-slate-600">
                  <tr>
                    {[
                      "Cashier",
                      "Bills",
                      "Subtotal",
                      "Discount",
                      "GST",
                      "Sales value",
                      "Received",
                      "Payment mix",
                      "Details",
                    ].map((header) => (
                      <th key={header} className="px-4 py-3">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.rows.map((row) => {
                    const expanded = expandedCashiers.has(row.cashier);
                    return (
                      <CashierRow
                        key={row.cashier}
                        row={row}
                        expanded={expanded}
                        onToggle={() => toggleCashier(row.cashier)}
                      />
                    );
                  })}
                  {!report.rows.length && (
                    <tr>
                      <td
                        colSpan="9"
                        className="px-4 py-16 text-center text-sm text-slate-500"
                      >
                        No POS sales were found for this period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      <ReportFilterModal
        open={showFilters}
        title="Cashier-wise POS filters"
        description="This report uses POS bills only. Series is not applicable. Generate applies the dates and closes this window."
        loading={loading}
        onApply={generate}
        onClose={closeFilters}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="From date">
            <input
              type="date"
              value={draftFilters.from}
              onChange={(event) =>
                setDraftFilters((current) => ({
                  ...current,
                  from: event.target.value,
                }))
              }
              className="report-input"
            />
          </Field>
          <Field label="To date">
            <input
              type="date"
              value={draftFilters.to}
              onChange={(event) =>
                setDraftFilters((current) => ({
                  ...current,
                  to: event.target.value,
                }))
              }
              className="report-input"
            />
          </Field>
        </div>
      </ReportFilterModal>
    </div>
  );
}

function CashierRow({ row, expanded, onToggle }) {
  return (
    <>
      <tr
        onClick={onToggle}
        className="cursor-pointer border-b border-slate-100 text-slate-700 transition hover:bg-primary-50/30"
        title="Click anywhere on this row to view bills"
      >
        <td className="px-4 py-3 font-semibold text-slate-950">
          {row.cashier}
        </td>
        <td className="px-4 py-3 text-right">{row.billCount}</td>
        <td className="px-4 py-3 text-right">{money(row.subtotal)}</td>
        <td className="px-4 py-3 text-right">{money(row.discount)}</td>
        <td className="px-4 py-3 text-right">{money(row.taxAmount)}</td>
        <td className="px-4 py-3 text-right font-semibold">
          {money(row.totalAmount)}
        </td>
        <td className="px-4 py-3 text-right font-semibold">
          {money(row.paidAmount)}
        </td>
        <td className="px-4 py-3">
          {Object.entries(row.payments).map(([method, amount]) => (
            <p key={method} className="whitespace-nowrap">
              {method}: {money(amount)}
            </p>
          ))}
        </td>
        <td className="px-4 py-3">
          <span className="inline-flex items-center gap-2 rounded-lg border border-primary-200 px-3 py-2 text-xs font-semibold text-primary-800">
            {expanded ? <FiChevronUp /> : <FiChevronDown />}
            {expanded ? "Hide bills" : "View bills"}
          </span>
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-slate-200 bg-slate-50">
          <td colSpan="9" className="p-4">
            <CashierBillLines bills={row.bills} />
          </td>
        </tr>
      )}
    </>
  );
}

function CashierBillLines({ bills }) {
  const lines = bills.flatMap((bill) =>
    bill.items.map((item) => ({ ...item, bill })),
  );

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
      <table className="min-w-[2450px] w-full text-xs">
        <thead className="bg-slate-100 text-left font-bold uppercase tracking-wide text-slate-500">
          <tr>
            {[
              "Bill date",
              "Bill no.",
              "Counter",
              "Store",
              "Bill total",
              "Received",
              "Agent",
              "Company",
              "Product",
              "Product code",
              "Category",
              "SKU",
              "Barcode",
              "Variant",
              "Colour",
              "Size",
              "Qty",
              "MRP",
              "Sale rate",
              "Discount",
              "GST rate",
              "GST amount",
              "Net amount",
            ].map((header) => (
              <th
                key={header}
                className="whitespace-nowrap border-b border-slate-200 px-3 py-2.5"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lines.map(({ bill, ...item }) => (
            <tr
              key={item.id}
              className="border-b border-slate-100 text-slate-700"
            >
              <td className="whitespace-nowrap px-3 py-2.5">
                {new Date(bill.completedAt).toLocaleString("en-IN")}
              </td>
              <td className="px-3 py-2.5 font-mono font-semibold text-slate-900">
                {bill.receiptNumber}
              </td>
              <td className="px-3 py-2.5">{bill.counter}</td>
              <td className="px-3 py-2.5">{bill.store}</td>
              <td className="px-3 py-2.5 text-right">
                {money(bill.totalAmount)}
              </td>
              <td className="px-3 py-2.5 text-right">
                {money(bill.paidAmount)}
              </td>
              <td className="px-3 py-2.5 font-semibold text-primary-900">
                {item.agent || "—"}
              </td>
              <td className="px-3 py-2.5">{item.companyName || "—"}</td>
              <td className="px-3 py-2.5 font-semibold text-slate-900">
                {item.productName}
              </td>
              <td className="px-3 py-2.5 font-mono">
                {item.productCode || "—"}
              </td>
              <td className="px-3 py-2.5">{item.category || "—"}</td>
              <td className="px-3 py-2.5 font-mono">{item.sku}</td>
              <td className="px-3 py-2.5 font-mono">{item.barcode || "—"}</td>
              <td className="px-3 py-2.5">{item.variant || "—"}</td>
              <td className="px-3 py-2.5">{item.color || "—"}</td>
              <td className="px-3 py-2.5">{item.size || "—"}</td>
              <td className="px-3 py-2.5 text-right">
                {quantity(item.quantity)}
              </td>
              <td className="px-3 py-2.5 text-right">
                {item.mrp === null ? "—" : money(item.mrp)}
              </td>
              <td className="px-3 py-2.5 text-right">{money(item.saleRate)}</td>
              <td className="px-3 py-2.5 text-right">{money(item.discount)}</td>
              <td className="px-3 py-2.5 text-right">{item.gstRate}%</td>
              <td className="px-3 py-2.5 text-right">
                {money(item.gstAmount)}
              </td>
              <td className="px-3 py-2.5 text-right font-bold text-emerald-700">
                {money(item.netAmount)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
